import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useNavigate } from 'react-router-dom';
import { Heart, ArrowLeft, BookOpen } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function GLPEducation({ onLogout }) {
  const navigate = useNavigate();
  const [glpData, setGlpData] = useState(null);
  const [loading, setLoading] = useState(true);
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    fetchGLPEducation();
  }, []);

  const fetchGLPEducation = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/glp-education`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGlpData(response.data);
    } catch (error) {
      console.error('Failed to load GLP education');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <p className="text-stone-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              data-testid="back-btn"
              onClick={() => navigate('/')}
              className="rounded-full"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-primary" />
              </div>
              <h1 className="text-2xl font-serif text-primary-900">PlayFirst</h1>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-4xl md:text-5xl font-serif font-normal text-primary-900 mb-4">
              Understanding GLP
            </h2>
            <p className="text-xl text-stone-600 leading-relaxed max-w-3xl mx-auto">
              Gestalt Language Processing: How your child learns language naturally
            </p>
          </div>

          {glpData && (
            <>
              <div className="space-y-8 mb-12">
                {glpData.stages.map((stage, index) => (
                  <motion.div
                    key={stage.stage}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    <Card className="p-8 bg-white rounded-3xl border-2 border-primary/10 hover:border-primary/30 transition-all">
                      <div className="flex items-start gap-4">
                        <div className="flex-shrink-0">
                          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-xl font-bold text-primary">{stage.stage}</span>
                          </div>
                        </div>
                        <div className="flex-1 space-y-4">
                          <div>
                            <h3 className="text-2xl font-serif text-primary-900 mb-2">{stage.name}</h3>
                            <p className="text-base text-stone-700 leading-relaxed">{stage.description}</p>
                          </div>

                          <div className="grid md:grid-cols-2 gap-4">
                            <div className="p-4 bg-blue-50 rounded-2xl">
                              <h4 className="text-sm font-bold text-blue-900 mb-2">What to Expect</h4>
                              <p className="text-sm text-blue-800">{stage.what_to_expect}</p>
                            </div>
                            <div className="p-4 bg-green-50 rounded-2xl">
                              <h4 className="text-sm font-bold text-green-900 mb-2">How to Support</h4>
                              <p className="text-sm text-green-800">{stage.how_to_support}</p>
                            </div>
                          </div>

                          <div className="p-4 bg-gradient-to-br from-secondary/5 to-secondary/10 rounded-2xl">
                            <p className="font-accent text-lg text-primary-800 text-center">
                              {stage.parent_friendly}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Card>
                  </motion.div>
                ))}
              </div>

              <Card className="p-8 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-3xl border-2 border-primary/20">
                <h3 className="text-2xl font-serif text-primary-900 mb-6 text-center">Key Principles</h3>
                <div className="space-y-3">
                  {glpData.key_principles.map((principle, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
                          <span className="text-xs font-bold text-primary">✓</span>
                        </div>
                      </div>
                      <p className="text-base text-stone-700 leading-relaxed">{principle}</p>
                    </div>
                  ))}
                </div>
              </Card>

              {/* Community Libraries Section */}
              <Card className="p-8 mt-8 bg-gradient-to-br from-purple-50 to-blue-50 rounded-3xl border-2 border-purple-200" data-testid="community-libraries-section">
                <h3 className="text-2xl font-serif text-primary-900 mb-6 text-center">How PlayFirst Grows With You</h3>
                
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="bg-white p-6 rounded-2xl">
                    <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                      <span className="text-2xl">🎮</span>
                    </div>
                    <h4 className="font-semibold text-lg text-purple-900 mb-2">Activity Library</h4>
                    <p className="text-sm text-stone-600 leading-relaxed">
                      Every activity you add to PlayFirst becomes part of our shared community library. 
                      When you create a new play-based activity, tag it with communication functions and regulation levels, 
                      it's saved for other therapists to discover and use. <strong>You're not just organizing your own practice 
                      — you're contributing to a growing bank of GLP-informed activities</strong> that helps all PlayFirst therapists 
                      serve their families better.
                    </p>
                  </div>
                  
                  <div className="bg-white p-6 rounded-2xl">
                    <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                      <span className="text-2xl">📚</span>
                    </div>
                    <h4 className="font-semibold text-lg text-emerald-900 mb-2">Resource Library</h4>
                    <p className="text-sm text-stone-600 leading-relaxed">
                      Found a great article about GLP? A helpful video for parents? Upload it to the Resource Library! 
                      Every educational resource you share — articles, videos, handouts, presentations — becomes available to 
                      the entire PlayFirst community. <strong>Together, we're building the most comprehensive neurodiversity-affirming 
                      resource collection</strong> for speech therapists and the families they serve.
                    </p>
                  </div>
                </div>

                <div className="mt-6 text-center">
                  <p className="text-sm text-purple-700 italic">
                    "The more we share, the stronger our community becomes. Every contribution helps a therapist somewhere 
                    connect better with a child they serve."
                  </p>
                </div>
              </Card>

              {/* Privacy & Legal Section */}
              <Card className="p-6 mt-8 bg-white rounded-3xl border border-stone-200" data-testid="privacy-legal-section">
                <h3 className="text-xl font-serif text-primary-900 mb-4">Privacy & Legal</h3>
                <div className="flex flex-wrap gap-3">
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/hipaa')}
                    className="rounded-full"
                    data-testid="privacy-policy-link"
                  >
                    Privacy Policy
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/terms')}
                    className="rounded-full"
                  >
                    Terms of Service
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/refund-policy')}
                    className="rounded-full"
                  >
                    Refund Policy
                  </Button>
                </div>
              </Card>
            </>
          )}
        </motion.div>
      </main>
    </div>
  );
}
